import { useEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { labelEvery, niceMax, scale, ticks, SERIES_COLORS } from "../lib/charts";

export interface ChartSeries { label: string; values: (number | null)[] }
export interface ChartProps {
  title: string;
  /** One entry per slot along the x axis; `xTick` formats the axis label, `xTitle` the tooltip heading (defaults to `xTick`). */
  x: string[]; xTick: (x: string) => string; xTitle?: (x: string) => string;
  /** Up to five; slot i takes `colors[i]`, default SERIES_COLORS[i]. */
  series: ChartSeries[];
  format?: (v: number) => string;
  colors?: readonly string[];
}

const HEIGHT = 240;
export const PAD = { top: 8, right: 8, bottom: 24, left: 56 };
const MAX_BAR = 24, GAP = 2;
const DEFAULT_WIDTH = 640;

/** Container width, updated on resize where ResizeObserver exists (jsdom and thumbnails have none). */
function useWidth(): [RefObject<HTMLDivElement | null>, number] {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(DEFAULT_WIDTH);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => { const w = entries[0]?.contentRect.width; if (w) setWidth(Math.round(w)); });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

const fmt = (format: ChartProps["format"], v: number) => (format ? format(v) : v.toLocaleString());
const colorOf = (p: ChartProps, i: number) => (p.colors ?? SERIES_COLORS)[i];
const tooltip = (p: ChartProps, i: number) => {
  const head = (p.xTitle ?? p.xTick)(p.x[i]);
  const parts = p.series.map((s) => (s.values[i] === null ? null : `${fmt(p.format, s.values[i]!)} ${s.label}`)).filter((t): t is string => t !== null);
  return parts.length ? `${head}: ${parts.join(", ")}` : `${head}: no data`;
};

/** Legend (two or more series), then the same numbers as a table so nothing depends on colour or hover. */
function Chrome({ p, children }: { p: ChartProps; children: ReactNode }) {
  return (
    <div>
      {p.series.length >= 2 && (
        <ul className="mb-3 flex flex-wrap gap-x-4 gap-y-1 text-xs font-medium text-hs-text">
          {p.series.map((s, i) => <li key={s.label} className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: colorOf(p, i) }} aria-hidden />{s.label}</li>)}
        </ul>
      )}
      {children}
      <details className="mt-2 text-xs">
        <summary className="cursor-pointer text-hs-text-muted">Show as table</summary>
        <table className="mt-1 w-full text-left">
          <thead><tr><th className="py-0.5 pr-2 font-semibold"> </th>{p.series.map((s) => <th key={s.label} className="py-0.5 pr-2 font-semibold">{s.label}</th>)}</tr></thead>
          <tbody>{p.x.map((x, i) => <tr key={x}><td className="py-0.5 pr-2 text-hs-text-muted">{p.xTick(x)}</td>{p.series.map((s) => <td key={s.label} className="py-0.5 pr-2">{s.values[i] === null ? "—" : fmt(p.format, s.values[i]!)}</td>)}</tr>)}</tbody>
        </table>
      </details>
    </div>
  );
}

function Axes({ p, width, top, y }: { p: ChartProps; width: number; top: number; y: (v: number) => number }) {
  const every = labelEvery(p.x.length);
  const plotW = width - PAD.left - PAD.right;
  const slot = plotW / Math.max(1, p.x.length);
  return (
    <>
      {ticks(top).map((t) => (
        <g key={t}>
          <line x1={PAD.left} x2={width - PAD.right} y1={y(t)} y2={y(t)} className="stroke-hs-border" strokeWidth={1} />
          <text x={PAD.left - 6} y={y(t) + 3} textAnchor="end" className="fill-hs-text-muted" fontSize={10}>{fmt(p.format, t)}</text>
        </g>
      ))}
      {p.x.map((x, i) => (i % every === 0 ? <text key={x} x={PAD.left + slot * i + slot / 2} y={y(0) + 16} textAnchor="middle" className="fill-hs-text-muted" fontSize={10}>{p.xTick(x)}</text> : null))}
    </>
  );
}

/**
 * `valueLabels` prints each bar's value above it, `xSub` a second line under an x label (a delta, say),
 * `highlight` shades one slot's background, `barWidth` widens the bars (default 24px); all off by default.
 */
export function BarChart(p: ChartProps & { stacked?: boolean; valueLabels?: boolean; xSub?: (x: string, i: number) => string | null; highlight?: number | null; barWidth?: number }) {
  const [ref, width] = useWidth();
  const n = p.x.length, k = p.series.length;
  const sub = p.xSub !== undefined;
  const height = sub ? HEIGHT + 14 : HEIGHT;
  const totals = p.x.map((_, i) => (p.stacked ? p.series.reduce((a, s) => a + (s.values[i] ?? 0), 0) : Math.max(0, ...p.series.map((s) => s.values[i] ?? 0))));
  const top = niceMax(Math.max(0, ...totals));
  const labelRoom = p.valueLabels ? 14 : 0;
  const plotH = HEIGHT - PAD.top - PAD.bottom - labelRoom, plotW = width - PAD.left - PAD.right;
  const yLen = scale(top, plotH);
  const y = (v: number) => PAD.top + labelRoom + plotH - yLen(v);
  const slot = plotW / Math.max(1, n);
  const perBar = p.stacked ? 1 : Math.max(1, k);
  const gap = slot < perBar * 2 + GAP * (perBar + 1) ? 0 : GAP;
  const bar = Math.min(p.barWidth ?? MAX_BAR, Math.max(1, (slot - gap * (perBar + 1)) / perBar));
  const groupW = bar * perBar + gap * (perBar - 1);
  return (
    <Chrome p={p}>
      <div ref={ref}>
        <svg width={width} height={height} role="img" aria-label={p.title} className="block">
          {p.highlight !== undefined && p.highlight !== null && p.highlight < n && (
            <rect data-highlight x={PAD.left + slot * p.highlight} y={PAD.top} width={slot} height={plotH + labelRoom} className="fill-hs-primary-soft/50" />
          )}
          <Axes p={p} width={width} top={top} y={y} />
          {p.x.map((x, i) => {
            const x0 = PAD.left + slot * i + (slot - groupW) / 2;
            let stackBase = 0;
            return (
              <g key={x}>
                {p.series.map((s, si) => {
                  const v = s.values[i] ?? 0;
                  if (v <= 0) return null;
                  const h = Math.max(0, yLen(v) - (p.stacked && stackBase > 0 ? GAP : 0));
                  const yTop = p.stacked ? y(stackBase + v) + (stackBase > 0 ? GAP : 0) : y(v);
                  const bx = p.stacked ? x0 : x0 + si * (bar + gap);
                  if (p.stacked) stackBase += v;
                  return (
                    <g key={s.label}>
                      <rect data-series={si} x={bx} y={yTop} width={bar} height={h} rx={3} fill={colorOf(p, si)} />
                      {p.valueLabels && !p.stacked && <text x={bx + bar / 2} y={yTop - 4} textAnchor="middle" className="fill-hs-text" fontSize={11} fontWeight={600}>{fmt(p.format, v)}</text>}
                    </g>
                  );
                })}
                {sub && p.xSub!(x, i) && <text x={PAD.left + slot * i + slot / 2} y={height - 6} textAnchor="middle" className="fill-hs-text-muted" fontSize={10}>{p.xSub!(x, i)}</text>}
                <rect x={PAD.left + slot * i} y={PAD.top} width={slot} height={plotH + labelRoom} fill="transparent"><title>{tooltip(p, i)}</title></rect>
              </g>
            );
          })}
        </svg>
      </div>
    </Chrome>
  );
}

/** A labelled run of x slots (indexes, inclusive); neighbouring bands are split by a divider. */
export interface ChartBand { from: number; to: number; label: string }
/** A dashed horizontal line; `below` shades the area under it down to zero and labels it. */
export interface ChartReference { value: number; label: string; below?: string }
const BAND_ROW = 14;

export function LineChart(p: ChartProps & { references?: ChartReference[]; bands?: ChartBand[] }) {
  const [ref, width] = useWidth();
  const n = p.x.length;
  const maxV = Math.max(0, ...p.series.flatMap((s) => s.values.filter((v): v is number => v !== null)), ...(p.references ?? []).map((r) => r.value));
  const top = niceMax(maxV);
  const bands = p.bands ?? [];
  const plotTop = PAD.top + (bands.length ? BAND_ROW : 0);
  const plotH = HEIGHT - plotTop - PAD.bottom, plotW = width - PAD.left - PAD.right;
  const yLen = scale(top, plotH);
  const y = (v: number) => plotTop + plotH - yLen(v);
  const slot = plotW / Math.max(1, n);
  const cx = (i: number) => PAD.left + slot * i + slot / 2;
  /** Consecutive non-null runs, so a gap in the data is a gap in the line. */
  const runs = (values: (number | null)[]) => {
    const out: [number, number][][] = []; let cur: [number, number][] = [];
    values.forEach((v, i) => { if (v === null) { if (cur.length) out.push(cur); cur = []; } else cur.push([cx(i), y(v)]); });
    if (cur.length) out.push(cur);
    return out;
  };
  const dots = n <= 31;
  return (
    <Chrome p={p}>
      <div ref={ref}>
        <svg width={width} height={HEIGHT} role="img" aria-label={p.title} className="block">
          {(p.references ?? []).map((r) => r.below && (
            <g key={`${r.label}-below`}>
              <rect data-below x={PAD.left} y={y(r.value)} width={plotW} height={y(0) - y(r.value)} className="fill-hs-warning-soft" />
              <text x={PAD.left + 6} y={y(0) - 6} className="fill-hs-text-muted" fontSize={10}>{r.below}</text>
            </g>
          ))}
          <Axes p={p} width={width} top={top} y={y} />
          {bands.map((b) => (
            <text key={b.label} x={PAD.left + (slot * (b.from + b.to + 1)) / 2} y={PAD.top + 10} textAnchor="middle" className="fill-hs-text-muted" fontSize={11} fontWeight={600}>{b.label}</text>
          ))}
          {bands.slice(1).map((b, i) => {
            const dx = PAD.left + (slot * (bands[i].to + 1 + b.from)) / 2;
            return <line key={`divider-${b.label}`} data-band-divider x1={dx} x2={dx} y1={PAD.top} y2={plotTop + plotH} className="stroke-hs-border" strokeWidth={1} strokeDasharray="2 3" />;
          })}
          {(p.references ?? []).map((r) => (
            <g key={r.label}>
              <line x1={PAD.left} x2={width - PAD.right} y1={y(r.value)} y2={y(r.value)} className="stroke-hs-text-muted" strokeWidth={1} strokeDasharray="4 3" />
              <text x={width - PAD.right} y={y(r.value) - 4} textAnchor="end" className="fill-hs-text-muted" fontSize={10}>{r.label}</text>
            </g>
          ))}
          {p.series.map((s, si) => runs(s.values).map((run, ri) => (
            <g key={`${s.label}-${ri}`}>
              <polyline data-series={si} points={run.map(([a, b]) => `${a},${b}`).join(" ")} fill="none" stroke={colorOf(p, si)} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
              {(dots || run.length === 1) && run.map(([a, b]) => <circle key={a} cx={a} cy={b} r={4} fill={colorOf(p, si)} stroke="white" strokeWidth={2} />)}
            </g>
          )))}
          {p.x.map((x, i) => <rect key={x} x={PAD.left + slot * i} y={plotTop} width={slot} height={plotH} fill="transparent"><title>{tooltip(p, i)}</title></rect>)}
        </svg>
      </div>
    </Chrome>
  );
}
